/**
 * Bettr scroll-reveal — Eight Sleep-style reveal for any element with
 * [data-reveal]. Pairs with assets/custom-scroll-reveal.css.
 *
 * Behavior:
 *   - On every scroll boundary crossing (intersecting → not, or back),
 *     toggles `.is-in-view` so the element re-animates each time it
 *     re-enters the viewport (Eight Sleep pattern).
 *   - `data-reveal-delay="N"` sets transition-delay in ms (sibling stagger).
 *   - `prefers-reduced-motion` is honoured by the CSS — no JS gate needed.
 *   - On Theme Editor section reload, re-observes any new [data-reveal]
 *     elements without leaking observers (one shared IntersectionObserver).
 *
 * Threshold + rootMargin chosen to match the eightsleep.com timing:
 *   - threshold: 0.15 — animation fires once 15% of the element is visible
 *   - rootMargin: '0px 0px -10% 0px' — counts the bottom 10% of the viewport
 *     as outside, so the reveal starts slightly before the element is
 *     fully on-screen
 */
(function () {
  'use strict';

  /* CSS scroll-driven animations handle plain [data-reveal] in modern
     Chromium / Firefox >= 133. We still observe [data-reveal-delay]
     elements there because scroll-timeline ignores ms-based delays —
     those elements opt out of the CSS path via an override in the
     inline @supports block in layout/theme.liquid. */
  var supportsScrollTimeline = (
    typeof CSS !== 'undefined' &&
    CSS.supports &&
    CSS.supports('animation-timeline', 'view()')
  );
  var SELECTOR = supportsScrollTimeline
    ? '[data-reveal][data-reveal-delay]'
    : '[data-reveal]';

  if (!('IntersectionObserver' in window)) {
    /* Old browser: just reveal everything immediately. */
    document.querySelectorAll(SELECTOR).forEach(function (el) {
      el.classList.add('is-in-view');
    });
    return;
  }

  var observed = new WeakSet();

  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var el = entry.target;
      if (entry.isIntersecting) {
        var delay = el.getAttribute('data-reveal-delay');
        if (delay) {
          var d = parseInt(delay, 10);
          if (!isNaN(d) && d > 0) el.style.transitionDelay = d + 'ms';
        }
        el.classList.add('is-in-view');
      } else {
        /* Re-trigger on next entry — clear the class and the stagger delay
           so the next animation starts cleanly. */
        el.classList.remove('is-in-view');
        el.style.transitionDelay = '';
      }
    }
  }

  var observer = new IntersectionObserver(onIntersect, {
    threshold: 0.15,
    rootMargin: '0px 0px -10% 0px'
  });

  function sweep(root) {
    var scope = root || document;
    var els = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (observed.has(el)) continue;
      observed.add(el);
      observer.observe(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sweep(); });
  } else {
    sweep();
  }

  /* Theme Editor: scope the sweep to the reloaded section so we don't
     re-walk the whole document. */
  document.addEventListener('shopify:section:load', function (e) {
    sweep(e.target);
  });
})();
